import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { SidebarItem } from '../interfaces';

export interface Locale {
  lang: string;
  data: any;
}

const LOCALIZATION_LOCAL_STORAGE_KEY = 'language';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  readonly LANGUAGES = {
    eng: {
      code: 'en',
      icon: '/assets/flags/united-kingdom.svg',
    },
    gr: {
      code: 'gr',
      icon: '/assets/flags/greece.svg',
    },
  };

  readonly lang = signal<'eng' | 'gr'>(this.getInitialLang());
  readonly translationsLoaded = signal(false);

  readonly flagUrl = computed(() => this.LANGUAGES[this.lang()].icon);

  get onLangChange() {
    return this.translates.onLangChange;
  }

  constructor(
    private translates: TranslateService,
  ) {
    translates.addLangs(['en', 'gr']);
    translates.setDefaultLang('en');

    effect(() => {
      const selectedLangKey = this.lang(); // 'eng' or 'gr'
      const selectedLang = this.LANGUAGES[selectedLangKey].code as 'en' | 'gr';

      this.translationsLoaded.set(false);
      this.translates.use(selectedLang).subscribe(() => {
        this.translationsLoaded.set(true);
      });
      localStorage.setItem(LOCALIZATION_LOCAL_STORAGE_KEY, selectedLangKey);
    });
  }

  translate(langKey: 'eng' | 'gr') {
    this.lang.set(langKey);
  }

  toggleLanguage(): void {
    this.lang.update((current) => (current === 'eng' ? 'gr' : 'eng'));
  }

  getTranslation(value: string): string {
    return this.translates.instant(value);
  }
  getTranslations(values: string[]): Observable<Record<string, string>> {
    return this.translates.get(values);
  }

  private getInitialLang(): 'eng' | 'gr' {
    const stored = localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY);
    return stored === 'gr' ? 'gr' : 'eng';
  }
}
